import { useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { Box, Grid } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { AtsGetJobPostingSettingsDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { JobMultiPostErrors } from 'modules/ats/components/JobMultiPostErrors'
import { ArchiveJobPostingModal } from 'modules/ats/components/job_postings/ArchiveJobPostingModal'
import { CancelJobPostingModal } from 'modules/ats/components/job_postings/CancelJobPostingModal'
import { PublishJobPostingModal } from 'modules/ats/components/job_postings/PublishJobPostingModal'
import { PublishedJobPostingModal } from 'modules/ats/components/job_postings/PublishJobPostingModal/PublishedJobPostingModal'
import { useEvaluationFormsFeatureAvailability } from 'modules/ats/hooks/features/useEvaluationFormsFeatureAvailability'
import { CompanyPageNotConfiguredModal } from 'modules/company_page/components/CompanyPageNotConfiguredModal'
import { useCompanyPageSettings } from 'modules/company_page/hooks/useCompanyPageSettings'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { JobPostingSettingsSkeleton } from './JobPostingSettingsSkeleton'
import { SectionComponent } from './SectionComponent'
import { SectionItem, SectionSelector } from './SectionSelector'

const JobPostingSettings = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { jobId } = useParams<{ jobId: string }>()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { publicUrl } = useCompanyPageSettings()
  const location = useLocation()
  const careersUrl = publicUrl || ''
  const { company, access } = useCurrentSession()
  const companyId = company.id

  const [showPublishModal, setShowPublishModal] = useState(false)
  const [showCompanyPageWarning, setShowCompanyPageWarning] = useState(false)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showPublishedModal, setShowPublishedModal] = useState(
    location.state?.showPublishedModal ?? false
  )

  const { featureAvailability: smartActionsAvailability } = useFeatureAvailability(
    Features.ats_smart_actions
  )
  const evaluationFormsAvailability = useEvaluationFormsFeatureAvailability()

  const { data } = useQuery(AtsGetJobPostingSettingsDocument, {
    variables: {
      id: Number(jobId),
      accessId: access.id,
      companyId,
    },
    skip: !jobId,
  })

  const canManageJobPostings = data?.ats._policies.manageJobPostings.can

  const hiringRole = data?.ats.jobPosting?.currentHiringRole[0]?.hiringRole ?? 'editor'
  const isOwnerRole = canManageJobPostings || hiringRole === 'owner'

  if (hiringRole === 'reviewer' && !canManageJobPostings) {
    navigate(
      navigation.ats.jobs.jobsId.applications.fullPath({
        jobId: Number(jobId),
      })
    )
    return null
  }

  const sectionParam = isOwnerRole ? searchParams.get('section') ?? 'basic-info' : 'hiring-team'

  if (!jobId) {
    navigate(navigation.ats.jobs.fullPath())
    return null
  }

  const jobData = data?.ats.jobPosting

  const selectedSection = [
    'basic-info',
    'description',
    'application-form',
    'hiring-pipeline',
    'hiring-team',
    'internal-evaluation-forms',
    'smart-actions',
  ].includes(sectionParam)
    ? sectionParam
    : 'basic-info'

  const sections: SectionItem[] = [
    {
      label: i18n.t('ats.job_posting_settings.sections.basic_information'),
      icon: 'file-filled',
      value: 'basic-info',
      disabled: !isOwnerRole,
    },
    {
      label: i18n.t('ats.job_posting_settings.sections.description'),
      icon: 'align-text-left',
      value: 'description',
      disabled: !isOwnerRole,
    },
    {
      label: i18n.t('ats.job_posting_settings.sections.application_form'),
      icon: 'question-circle-line',
      value: 'application-form',
      disabled: !isOwnerRole,
    },
    {
      label: i18n.t('ats.job_posting_settings.sections.hiring_pipeline'),
      icon: 'ol-list',
      value: 'hiring-pipeline',
      disabled: !isOwnerRole,
    },
    {
      label: i18n.t('ats.job_posting_settings.sections.hiring_team'),
      icon: 'manager',
      value: 'hiring-team',
    },
  ]

  if (evaluationFormsAvailability) {
    sections.push({
      label: i18n.t('ats.job_posting_settings.sections.internal_evaluation_forms'),
      icon: 'comment',
      value: 'internal-evaluation-forms',
      disabled: !isOwnerRole,
    })
  }

  if (smartActionsAvailability.available) {
    sections.push({
      label: i18n.t('ats.job_posting_settings.sections.smart_actions'),
      icon: 'lightbulb',
      value: 'smart-actions',
      disabled: !isOwnerRole,
    })
  }

  const onClosePublishedJobPostingModal = () => {
    setShowPublishedModal(false)
    navigate(location.pathname, { replace: true })
  }

  const handleSectionChange = (value: string) => {
    if (value === 'hiring-team') {
      track(trackingEvents.ats.visitHiringTeam, {
        ats_job_id: Number(jobId),
        company_id: companyId,
        user_id: access.id,
      })
    }
    setSearchParams({ section: value })
  }

  return (
    <ResponsivePage.Body showHeader={false}>
      {!jobData ? (
        <JobPostingSettingsSkeleton />
      ) : (
        <Box gap="s32">
          <JobMultiPostErrors atsJobPostingId={Number(jobId)} locationId={jobData.location?.id} />

          <Grid variant="fixed">
            <Box gridColSpan={{ xs: 3, s: 3 }} gap="s8">
              {sections.map((section) => (
                <SectionSelector
                  key={section.label}
                  label={section.label}
                  value={section.value}
                  selected={section.value === selectedSection}
                  icon={section.icon}
                  disabled={section.disabled}
                  onChange={handleSectionChange}
                />
              ))}
            </Box>
            <Box gridColSpan={{ s: 9 }}>
              <SectionComponent
                selected={selectedSection}
                jobId={Number(jobId)}
                companyId={companyId}
              />
            </Box>
          </Grid>

          {showPublishModal && (
            <PublishJobPostingModal
              job={jobData}
              onSuccess={(status) => {
                setShowPublishModal(false)
                status === 'published' && setShowPublishedModal(true)
              }}
              onClose={() => setShowPublishModal(false)}
            />
          )}

          {showPublishedModal && (
            <PublishedJobPostingModal
              id={jobData.id}
              onClose={() => onClosePublishedJobPostingModal()}
            />
          )}

          {showArchiveModal && (
            <ArchiveJobPostingModal
              careersUrl={careersUrl}
              id={jobData.id}
              onClose={() => setShowArchiveModal(false)}
            />
          )}
          {showCancelModal && (
            <CancelJobPostingModal
              careersUrl={careersUrl}
              id={jobData.id}
              onClose={() => setShowCancelModal(false)}
            />
          )}

          {showCompanyPageWarning && (
            <CompanyPageNotConfiguredModal onClose={() => setShowCompanyPageWarning(false)} />
          )}
        </Box>
      )}
    </ResponsivePage.Body>
  )
}

export default JobPostingSettings
